import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileBarChart } from 'lucide-react';
import type { Page, Report } from '@simpleacs/shared';
import { api } from '../lib/api';
import { ReportDetailDialog } from './ReportDetailDialog';
import { formatRelative } from '../lib/format';
import {
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

const KIND_LABELS: Record<string, string> = {
  config_drift: 'Config drift',
  router_drift: 'Changed on the router',
  backup_restore: 'Backup restore',
  optical_degradation: 'Optical degradation',
  pon_degradation: 'PON-wide light drop',
  inventory_sweep: 'Inventory sweep',
  onu_migration: 'ONU migration',
};

const STATUS_TONES: Record<Report['status'], 'warn' | 'success' | 'danger' | 'neutral'> = {
  open: 'warn',
  fixed: 'success',
  fix_failed: 'danger',
  dismissed: 'neutral',
};

/**
 * Findings the platform produced about the network — as opposed to the action
 * log, which records what people did. Nightly drift checks and restores land
 * here; more report kinds will join them.
 */
export function ReportsPage() {
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [openReport, setOpenReport] = useState<Report | null>(null);

  const query = useQuery({
    queryKey: ['reports', kind, status],
    queryFn: () =>
      api.get<Page<Report>>('/reports', {
        kind: kind || undefined,
        status: status || undefined,
      }),
  });
  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="OLT log"
        description="What the platform found on the OLTs. Open a finding to see its evidence and act on it — reconcile drift to the chassis, or dismiss it."
      />
      <Card
        title={`Findings (${items.length})`}
        action={
          <span className="flex gap-2">
            <select className="input w-44" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">All kinds</option>
              <option value="config_drift">Config drift</option>
              <option value="router_drift">Changed on the router</option>
              <option value="backup_restore">Backup restore</option>
              <option value="inventory_sweep">Inventory sweep</option>
              {/* A migration is filed here whether it landed or was refused —
                  a duplicate that was NOT moved is the row an operator most
                  needs to find afterwards. */}
              <option value="onu_migration">ONU migration</option>
            </select>
            <select className="input w-36" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="open">Open</option>
              <option value="fixed">Fixed</option>
              <option value="fix_failed">Fix failed</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </span>
        }
      >
        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : query.error ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<FileBarChart className="size-8" />}
            title="No findings"
            description="The nightly checks have nothing to report — that is the good outcome."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Kind</th>
              <th className="th">Subject</th>
              <th className="th">Summary</th>
              <th className="th">Status</th>
              <th className="th">When</th>
            </THead>
            <TBody>
              {items.map((report) => (
                <TRow key={report.id} onClick={() => setOpenReport(report)}>
                  <td className="td">
                    <Pill tone="info">{KIND_LABELS[report.kind] ?? report.kind}</Pill>
                  </td>
                  <td className="td font-mono text-xs">{report.subject}</td>
                  <td className="td text-muted">{report.summary}</td>
                  <td className="td">
                    <Pill state tone={STATUS_TONES[report.status]}>{report.status.replace('_', ' ')}</Pill>
                  </td>
                  <td className="td text-muted">{formatRelative(report.createdAt)}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {/* Reconcile and Dismiss both close the dialog and report through the
          tasks tray, so the page keeps no toast of its own. */}
      {openReport && (
        <ReportDetailDialog report={openReport} onClose={() => setOpenReport(null)} />
      )}
    </>
  );
}
